import React, { useRef, useCallback, useEffect, useState } from 'react';
import { withStyles } from '@glu/theming';
import { useTrapFocus } from '@glu/utilities-react';
import { Slide } from '@glu/transitions-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  CheckmarkCircleNextIcon,
  XCircleIcon,
  ExclamationCircleNextIcon,
  ExclamationTriangleNextIcon,
  InfoCircleNextIcon
} from '@glu/icons-react';
import useKeyHandler from 'hooks/useKeyHandler';
import useModalFocus from 'hooks/useModalFocus';

import styles from './fullscreenModal.styles';

const icons = {
  success: CheckmarkCircleNextIcon,
  error: ExclamationTriangleNextIcon,
  warning: ExclamationCircleNextIcon,
  info: InfoCircleNextIcon,
  none: ''
};

export function tab(event) {
  if (!event) {
    /* istanbul ignore next */
    return false;
  }

  if (event.key) {
    /* istanbul ignore next */
    return event.key.toLowerCase() === 'tab';
  }

  return (event.keyIdentifier || event.keyCode) === 9;
}

const FullscreenModal = ({
  children,
  className,
  classes,
  dataQa,
  header,
  icon,
  mountOnEnter,
  onHide,
  show,
  showIcon,
  transition: Transition,
  unmountOnExit,
  variant,
  verticalPosition,
  withParams
}) => {
  const [transitionIn, setTransitionIn] = useState(true);
  const Icon = icon ? icons[icon] : icons.error;
  const [initial, setInitial] = useState(true);
  const modalRootRef = useRef();

  useEffect(() => {
    document.body.classList[show ? 'add' : 'remove']('modal--opened');
    return () => {
      document.body.classList.remove('modal--opened');
    };
  }, [show]);

  useEffect(() => setInitial(false), []);
  useEffect(() => {
    if (!transitionIn) {
      const timeout = setTimeout(() => {
        setTransitionIn(true);

        if (!initial) {
          onHide();
        }
      }, 0.5);

      return () => {
        clearTimeout(timeout);
        document.body.classList.remove('modal--opened');
      };
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHide, transitionIn]);

  const closeModal = useCallback(
    (event) => {
      if (tab(event)) {
        /* istanbul ignore next */
        return;
      }

      if (typeof Transition === 'string') {
        /* istanbul ignore next */
        onHide();
        return;
      }

      setTransitionIn(false);
    },
    [Transition, onHide]
  );

  const transitionProps =
    typeof Transition === 'string'
      ? {}
      : {
          in: show && transitionIn,
          mountOnEnter,
          unmountOnExit
        };

  const getAriaLabel = () => 'Dialog';

  // Focus modal on open, focus previous element on close
  useModalFocus({ ref: modalRootRef, active: show });

  // Escape key to close modal
  useKeyHandler({ keyEvent: 'keyup', keyCode: 27, callback: closeModal });

  const onKeyDown = useTrapFocus({ ref: modalRootRef, active: show });

  return (
    <section className={classes.modalComponent}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        className={classNames(className, classes.root, { [classes.modalIn]: show })}
        data-qa={dataQa}
        role="dialog"
        ref={modalRootRef}
        style={{ top: `${verticalPosition}px` }}
        onKeyDown={onKeyDown}
        tabIndex="-1"
        aria-label={getAriaLabel()}
      >
        <Transition {...transitionProps}>
          <article
            className={classNames(classes.dialog, 'dialog')}
            role="document"
            data-qa="full-screen-modal"
          >
            <Button
              variant={TERTIARY}
              className={classes.close}
              onClick={closeModal}
              data-qa="full-screen-modal-close"
            >
              <span className="sr-only">Close Transfer Import dialog</span>
              <XCircleIcon title="Close Transfer Import dialog" aria-hidden focusable="false" />
            </Button>
            <div className={classNames(classes.content, classes[variant])}>
              <header className={classes.header}>
                {showIcon && <Icon className={classes.icon} />}
                <br />
                {typeof header === 'string' ? <h1 className={classes.title}>{header}</h1> : header}
              </header>

              <section className={classes.modalBody}>
                {withParams ? children({ modalRootRef: modalRootRef.current }) : children}
              </section>
            </div>
          </article>
        </Transition>
      </div>
    </section>
  );
};

FullscreenModal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  classes: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  closeButton: PropTypes.bool,
  dataQa: PropTypes.string,
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  header: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  hideBackdrop: PropTypes.bool,
  icon: PropTypes.string,
  mountOnEnter: PropTypes.bool,
  onHide: PropTypes.func,
  show: PropTypes.bool,
  showIcon: PropTypes.bool,
  theme: PropTypes.shape({}),
  transition: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.string,
    PropTypes.shape({ render: PropTypes.func.isRequired }),
    PropTypes.instanceOf(Slide)
  ]),
  unmountOnExit: PropTypes.bool,
  variant: PropTypes.oneOf(['info', 'success', 'error', 'warning']),
  withParams: PropTypes.bool,
  verticalPosition: PropTypes.number
};

FullscreenModal.defaultProps = {
  children: [],
  classes: {},
  className: '',
  closeButton: true,
  dataQa: null,
  footer: null,
  header: null,
  hideBackdrop: false,
  icon: '',
  mountOnEnter: true,
  // istanbul ignore next
  onHide() {},
  show: false,
  showIcon: true,
  theme: {},
  transition: Slide,
  unmountOnExit: true,
  variant: null,
  withParams: false,
  verticalPosition: 0
};

export default withStyles(styles)(FullscreenModal);
